import Categories from "features/tests/create/formFields/Categories";
import ClassroomSelect from "features/tests/create/formFields/ClassroomSelect";
import CreateTestConfiguration from "features/tests/create/formFields/TestConfiguration";
import TestDifficulty from "features/tests/create/formFields/TestDifficulty";
import TestExpire from "features/tests/create/formFields/TestExpire";
import GradeSelect from "features/tests/create/formFields/GradeSelect";
import IndividualTest from "features/tests/create/formFields/IndividualTest";
import ItemsNumber from "features/tests/create/formFields/ItemsNumber";
import StudentsSelect from "features/tests/create/formFields/StudentsSelect";
import TestType from "features/tests/create/formFields/TestType";
import {ICreateTestTrainingConfig} from "features/tests/create/components/_interfaces";
import TestStartDate from "../formFields/TestStartDate";
import TagSelect from "../formFields/TagSelect";
import TestDuration from "../formFields/TestDuration";
import CustomDuration from "../formFields/CustomDuration";
import {findMatch, selectedStandardsCount} from "../utils/functions";
import {useMemo} from "react";

const TestConfig = ({
                                type,
                                subjects,
                                grades,
                                createTestData,
                                setClassroomHandler,
                                setSelectedStudentsHandler,
                                setSelectAllStudentsHandler,
                                handleSelectChange,
                                handleInputChange,
                                setSubjectHandler,
                                setGradeHandler,
                                setSelectedCategoriesHandler,
                                setSelectedVariant,
                                handleTestStartDate,
                                loading,
                                errors,
                                tags,
                                selectedCategoriesCount,
                                setSelectedStandardsHandler,
                          }: ICreateTestTrainingConfig) => {
    const hasSelectedStudents = createTestData.users.some((el: any) => el.checked === true);
    const hasTag = Boolean(createTestData.tag.value);
    const hasExpire = Boolean(createTestData.expire);
    const hasSubject = Boolean(createTestData.subject.value);
    const hasGrade = Boolean(createTestData.grade.value);
    const hasCategories = findMatch(true, createTestData.categories);
    const totalSelectedCount = useMemo(() => {
        if(Number(createTestData.grade.value) >= 8) {
            return selectedCategoriesCount
        } else {
            return selectedStandardsCount(createTestData.categories)
        }
    }, [createTestData.categories]);

    const isLiteracy = [
        "literacy",
        "digitalLiteracy",
        "numericLiteracy",
        "scientificLiteracy",
    ].includes(type);

    const isBacOrEvaluate = type === "bac" || type === "evaluate";

    const showIndividualTest = [
        "training",
        "school",
        "bac",
        "evaluate",
    ].includes(type);

    const showSubject = (
        (type === "school" && hasExpire) ||
        (type === "training" && Boolean(createTestData.difficulty.value)) ||
        (type === "customTest" && Boolean(createTestData.custom_duration)) ||
        (isBacOrEvaluate && hasExpire)
    );

    const showSubjectsNumericLiteracy = (type === "numericLiteracy" && hasGrade);

    const showGrade = (
        (type === "training" && hasSubject && Boolean(createTestData.difficulty.value)) ||
        (["school", "customTest"].includes(type) && hasSubject) ||
        (isLiteracy && hasExpire)
    );

    const showCategories = (
        hasGrade &&
        type !== "customTest" &&
        type !== "numericLiteracy"
    );

    const showBacEvaluateCategories = isBacOrEvaluate && hasSubject;

    return (
        <>
            <ClassroomSelect
                errors={errors}
                setClassroomHandler={setClassroomHandler}
                classroom={createTestData.classroom}
            />
            {createTestData.classroom && (
                <StudentsSelect
                    errors={errors}
                    loading={loading}
                    users={createTestData.users}
                    selectedStudents={createTestData.users}
                    setSelectedStudentsHandler={setSelectedStudentsHandler}
                    setSelectAllStudentsHandler={setSelectAllStudentsHandler}
                />
            )}
            {hasSelectedStudents && (
                <>
                    <CreateTestConfiguration
                        testConfig={createTestData.test_config.value}
                        handleSelectChange={handleSelectChange}
                        errors={errors}
                    />

                    <TagSelect
                        errors={errors}
                        loading={loading}
                        tag={createTestData.tag.value}
                        tags={tags}
                        handleSelectChange={handleSelectChange}
                    />

                    {hasTag && (
                        <>
                            {showIndividualTest && (
                                <IndividualTest
                                    testType={type}
                                    errors={errors}
                                    individualTest={createTestData.individual_test.value}
                                    handleSelectChange={handleSelectChange}
                                />
                            )}

                            <TestStartDate
                                errors={errors}
                                startDate={createTestData.start_date}
                                handleTestStartDate={handleTestStartDate}
                            />

                            <TestExpire
                                expire={createTestData.expire}
                                handleInputChange={handleInputChange}
                                errors={errors}
                            />
                        </>
                    )}

                    {type === "training" && hasExpire && (
                        <TestDifficulty
                            errors={errors}
                            difficulty={createTestData.difficulty.value}
                            handleSelectChange={handleSelectChange}
                        />
                    )}

                    {(type === "customTest" && hasExpire ) && (
                        <CustomDuration
                            duration={createTestData.custom_duration}
                            handleInputChange={handleInputChange}
                            errors={errors}
                        />
                    )}

                    {showSubject && (
                            <TestType
                                loading={loading}
                                errors={errors}
                                subject={createTestData.subject.value}
                                subjects={subjects}
                                setSubjectHandler={setSubjectHandler}
                            />
                        )}

                    {showGrade && (
                        <GradeSelect
                            loading={loading}
                            errors={errors}
                            grade={createTestData.grade.value}
                            grades={grades}
                            setGradeHandler={setGradeHandler}
                        />
                    )}

                    {showSubjectsNumericLiteracy && (
                        <TestType
                            type={type === "numericLiteracy" ? type : undefined}
                            loading={loading}
                            errors={errors}
                            subject={createTestData.subject.value}
                            subjects={subjects}
                            setSubjectHandler={setSubjectHandler}
                        />
                    )}

                    {showCategories && (
                        <Categories
                            type={type}
                            loading={loading}
                            errors={errors}
                            categories={createTestData.categories}
                            grade={createTestData.grade.value}
                            setSelectedCategoriesHandler={setSelectedCategoriesHandler}
                            setSelectedStandardsHandler={setSelectedStandardsHandler ? setSelectedStandardsHandler : undefined}
                        />
                    )}

                    {showBacEvaluateCategories && (
                        <Categories
                            type={type}
                            loading={loading}
                            errors={errors}
                            categories={createTestData.categories}
                            variant={createTestData.variant.value}
                            setSelectedVariant={setSelectedVariant}
                            setSelectedCategoriesHandler={setSelectedCategoriesHandler}
                        />
                    )}

                    {type === "school" && hasCategories && (
                        <TestDuration
                            errors={errors}
                            itemsNumber={createTestData.test_duration.value}
                            handleSelectChange={handleSelectChange}
                            selectedCategoriesCount={totalSelectedCount}
                        />
                    )}

                    {type === "training" && hasCategories && (
                        <ItemsNumber
                            errors={errors}
                            itemsNumber={createTestData.items_number.value}
                            handleSelectChange={handleSelectChange}
                        />
                    )}

                    {(type === "training" && createTestData.items_number.value ) && (
                        <CustomDuration
                            duration={createTestData.custom_duration}
                            handleInputChange={handleInputChange}
                            errors={errors}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default TestConfig;
